import React, { useState, useEffect, useMemo } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { FiPlus, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { emailDraftService } from '@/_services/api/emailIntegration/emailDraft';
import EmailDraftsList from './EmailDraftsList';
import EmailComposer from './EmailComposer';

const DraftContainer = ({ chatroomId }) => {
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [composerOpen, setComposerOpen] = useState(false);
    const [composerKey, setComposerKey] = useState(0);

    const sortedDrafts = useMemo(
        () => [...drafts].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()),
        [drafts]
    );

    const fetchDrafts = async () => {
        if (!chatroomId) return;
        setLoading(true);
        try {
            const res = await emailDraftService.getChatroomEmailDrafts({
                chatroomId,
                status: 'draft',
            });
            setDrafts(res?.results || []);
        } catch (error) {
            toast.error('Failed to load email drafts');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrafts();
    }, [chatroomId]);

    const handleCreate = async () => {
        try {
            setIsCreating(true);
            const res = await emailDraftService.generateChatroomEmailDraft({
                chatroomId,
                payload: { topic: '', broadcast: false },
            });
            const draft = res?.draft || res;
            setDrafts((prev) => [draft, ...prev]);
            setSelectedDraft(draft);
            setComposerKey((prev) => prev + 1);
            setComposerOpen(true);
        } catch (error) {
            toast.error('Failed to create draft');
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpen = (draft) => {
        setSelectedDraft(draft);
        setComposerKey((prev) => prev + 1);
        setComposerOpen(true);
    };

    const handleDelete = async (draft) => {
        const result = await Swal.fire({
            title: 'Delete draft?',
            text: `Delete "${draft?.subject || '(no subject)'}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d33',
        });
        if (!result.isConfirmed) return;

        try {
            await emailDraftService.deleteChatroomEmailDraft(draft?.id || draft?._id);
            setDrafts((prev) => prev.filter((item) => String(item._id || item.id) !== String(draft?._id || draft?.id)));
            if (selectedDraft && String(selectedDraft._id || selectedDraft.id) === String(draft?._id || draft?.id)) {
                setSelectedDraft(null);
                setComposerOpen(false);
            }
            toast.success('Draft deleted');
        } catch (error) {
            toast.error(error?.message || 'Failed to delete draft');
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-end mb-3">
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={fetchDrafts}
                    disabled={loading || isCreating}
                    className="me-2"
                >
                    <FiRefreshCw className={loading ? 'spin' : ''} />
                </Button>
                <Button variant="primary" size="sm" onClick={handleCreate} disabled={loading || isCreating}>
                    {isCreating ? (
                        <Spinner animation="border" size="sm" className="me-2" />
                    ) : (
                        <FiPlus className="me-2" />
                    )}
                    Compose
                </Button>
            </div>

            <EmailDraftsList
                drafts={sortedDrafts}
                loading={loading}
                onOpenDraft={handleOpen}
                onDeleteDraft={handleDelete}
            />

            <EmailComposer
                key={composerKey}
                show={composerOpen}
                chatroomId={chatroomId}
                draftId={selectedDraft?.id || selectedDraft?._id}
                initialDraft={selectedDraft}
                onClose={() => {
                    setComposerOpen(false);
                    fetchDrafts();
                }}
                onCreated={(draft) => {
                    if (draft) {
                        setDrafts((prev) => {
                            const next = prev.filter(
                                (item) => String(item._id || item.id) !== String(draft._id || draft.id)
                            );
                            return [draft, ...next];
                        });
                    }
                }}
                onDeleteDraft={handleDelete}
            />
        </div>
    );
};

export default DraftContainer;
